import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Download } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { monthRange, downloadCsv, downloadPdf, fmt, toastExportNeedsLoad } from '../reportUtils';
import { useTenantCompanyName, ReportToolbar, ReportDocument, fmtRp } from '../ReportShell';
import { journalSourceLabel } from '../accountingUiConstants';

function journalTotal(j) {
  return (j.lines || []).reduce((s, l) => s + (l.debit || 0), 0);
}

export default function ReportJournalRegister() {
  const company = useTenantCompanyName();
  const { start: mStart, end: mEnd } = monthRange();
  const [jrStart, setJrStart] = useState(mStart);
  const [jrEnd, setJrEnd] = useState(mEnd);
  const [jr, setJr] = useState(null);

  useEffect(() => {
    setJr(null);
  }, [jrStart, jrEnd]);

  const loadJr = () => {
    const q = { start: jrStart, end: jrEnd };
    api.get('/tenant/accounting/reports/journal-register', { params: q }).then((r) => setJr(r.data)).catch(() => toast.error('Gagal daftar jurnal'));
  };

  const params = { start: jrStart, end: jrEnd };

  return (
    <div>
      <ReportToolbar>
        <div className="flex flex-col gap-1">
          <Label className="text-xs font-semibold text-slate-600">Dari</Label>
          <Input type="date" value={jrStart} onChange={(e) => setJrStart(e.target.value)} className="h-9 w-[150px]" />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs font-semibold text-slate-600">Sampai</Label>
          <Input type="date" value={jrEnd} onChange={(e) => setJrEnd(e.target.value)} className="h-9 w-[150px]" />
        </div>
        <Button type="button" onClick={loadJr} className="h-9">
          Muat data
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-9"
          onClick={() => {
            if (!jr) return toastExportNeedsLoad();
            downloadCsv('/tenant/accounting/reports/journal-register', params, 'daftar_jurnal.csv');
          }}
        >
          <Download className="mr-1 h-4 w-4" />
          CSV
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-9"
          onClick={() => {
            if (!jr) return toastExportNeedsLoad();
            downloadPdf('/tenant/accounting/reports/journal-register', params, 'daftar_jurnal.pdf');
          }}
        >
          PDF
        </Button>
      </ReportToolbar>

      {jr?.journals?.length ? (
        <ReportDocument
          companyName={company}
          title="DAFTAR JURNAL"
          subtitle="Indeks entri jurnal menurut tanggal"
          periodLabel={`Periode ${jrStart} s.d. ${jrEnd}`}
        >
          <table className="acct-rpt-table">
            <thead>
              <tr>
                <th style={{ width: '7rem' }}>Tanggal</th>
                <th style={{ width: '4rem' }}>No.</th>
                <th style={{ width: '8rem' }}>Sumber</th>
                <th>Keterangan / memo</th>
                <th className="acct-rpt-th-num" style={{ width: '8rem' }}>
                  Total (Rp)
                </th>
              </tr>
            </thead>
            <tbody>
              {jr.journals.map((j) => (
                <tr key={j.id}>
                  <td className="font-mono text-xs whitespace-nowrap">{j.entry_date}</td>
                  <td className="acct-rpt-num">{j.entry_number}</td>
                  <td className="text-xs">{journalSourceLabel(j.source_type)}</td>
                  <td>{j.memo}</td>
                  <td className="acct-rpt-num font-medium">{fmtRp(journalTotal(j))}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="acct-rpt-summary-bar mt-4">
            <strong>Total entri:</strong> {jr.journals.length} jurnal · <strong>Jumlah nominal (total debit per jurnal):</strong> Rp{' '}
            {fmt(jr.journals.reduce((s, j) => s + journalTotal(j), 0))}
          </p>
        </ReportDocument>
      ) : null}

      {jr && !jr.journals?.length && <div className="acct-rpt-empty">Tidak ada jurnal pada periode ini.</div>}

      {!jr && <div className="acct-rpt-empty">Pilih periode lalu klik <strong>Muat data</strong>.</div>}
    </div>
  );
}
